import { motion } from "framer-motion";
import { ArrowDownRight, ShieldCheck, Clock, FileCheck } from "lucide-react";
import Hero3D from "./Hero3D";
import { MaskLines, EASE } from "./Reveal";
import { analytics } from "../lib/analytics";

const TRUST = [
  { icon: ShieldCheck, label: "Confidentiality-first, IRS §7216-aware" },
  { icon: FileCheck, label: "US GAAP & IRS-trained team" },
  { icon: Clock, label: "All US time zones covered" },
];

export default function Hero({ onNav }) {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0" style={{ maskImage: "linear-gradient(to left, black 55%, transparent 94%)", WebkitMaskImage: "linear-gradient(to left, black 55%, transparent 94%)" }}>
        <Hero3D />
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_18%_30%,rgba(37,99,235,0.09),transparent_55%),radial-gradient(ellipse_at_85%_75%,rgba(255,107,0,0.08),transparent_50%)]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="inline-flex items-center gap-3 rounded-full glass px-4 py-2 mb-8 shadow-soft"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-torange opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-torange" />
          </span>
          <span data-testid="hero-eyebrow" className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.28em] text-slate-600">
            TaccTax Solution Pvt. Ltd. — Offshore KPO
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold leading-[0.98] tracking-tight text-[clamp(2rem,5.75vw,5.4rem)] max-w-4xl text-ink">
          <MaskLines
            delay={0.35}
            lines={[
              <span key="1">Transforming</span>,
              <span key="2" className="text-slate-400">complexity</span>,
              <span key="3">
                into <span className="text-gradient">clarity.</span>
              </span>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
        >
          The India-based offshore KPO partner built exclusively for US CPA and
          accounting firms — bookkeeping, taxation and payroll, delivered inside
          your workflow, on your deadlines.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => { analytics.ctaClick("book_discovery_call", "hero"); onNav("#contact"); }}
            className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-bluedark via-bluze to-torange bg-[length:150%_100%] bg-left hover:bg-right px-8 py-4 text-sm font-bold text-white shadow-lift transition-all duration-500 cursor-pointer"
          >
            Book a Discovery Call
            <ArrowDownRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => { analytics.ctaClick("explore_rate_sheet", "hero"); onNav("#rates"); }}
            className="inline-flex items-center gap-2.5 rounded-full border border-ink/15 bg-white/60 px-8 py-4 text-sm font-bold text-slate-700 hover:border-torange/60 hover:text-ink hover:bg-white transition-all duration-300 cursor-pointer"
          >
            Explore Rate Sheet
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
        >
          {TRUST.map((t) => (
            <div key={t.label} className="flex items-center gap-2 text-xs text-slate-500">
              <t.icon size={15} className="text-bluze" />
              <span>{t.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden lg:flex flex-col items-center gap-3"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 [writing-mode:vertical-rl]">scroll</span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="h-12 w-px bg-gradient-to-b from-bluze to-torange"
        />
      </motion.div>
    </section>
  );
}
